'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import { useAuth } from '@/providers/userContext';
import Toggle from '@/components/atoms/Toggle';
import { IconArrow } from '@/components/atoms/Icons';
import { haversineMiles, minutesFromMiles } from '@/shared/geo';
import Abstractcontourpattern from '@/assets/images/abstract-contour-pattern.png';
import MoAbstractcontourpattern from '@/assets/images/mo-abstract-contour-pattern.png';
import HeartCard from './HeartCard';
export default function HeartsNearby({
  communityName,
  communityState,
  communityId,
  entity,
  pageType
}) {
  const [filter, setFilter] = useState(pageType === 'heart' ? 'unseen' : 'all');
  const [hasScrolled, setHasScrolled] = useState(false);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [maxWidth, setMaxWidth] = useState('100%');
  const {
    user
  } = useAuth();
  const [displayHearts, setDisplayHearts] = useState([]);
  const [emptyMessage, setEmptyMessage] = useState('');
  const [allHearts, setAllHearts] = useState([]);

  /* ---------------- EMBLA ---------------- */
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
    dragFree: false
  });
  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
    setHasScrolled(true);
  }, [emblaApi]);

  /* -------- Arrow enable/disable -------- */
  useEffect(() => {
    if (!emblaApi) return;
    const onUpdate = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    emblaApi.on('init', onUpdate);
    emblaApi.on('reInit', onUpdate);
    emblaApi.on('select', onUpdate);
    onUpdate();
    return () => {
      emblaApi.off('init', onUpdate);
      emblaApi.off('reInit', onUpdate);
      emblaApi.off('select', onUpdate);
    };
  }, [emblaApi]);

  /* ---------------- Width logic ---------------- */
  const containerRef = useRef(null);
  const [carouselWidth, setCarouselWidth] = useState('100vw');
  useEffect(() => {
    const calculateWidth = () => {
      if (!containerRef.current) return;
      const container = containerRef.current;
      const styles = window.getComputedStyle(container);
      const left = container.offsetLeft + parseFloat(styles.paddingLeft || '0');
      setCarouselWidth(`calc(100vw - ${left}px)`);
    };
    calculateWidth();
    window.addEventListener('resize', calculateWidth);
    return () => window.removeEventListener('resize', calculateWidth);
  }, []);
  useEffect(() => {
    const updateMaxWidth = () => {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      setMaxWidth(scrollbarWidth > 0 ? `calc(100vw - ${scrollbarWidth}px)` : '100vw');
    };
    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  /* ---------------- DOTS LOGIC ---------------- */
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  useEffect(() => {
    if (!emblaApi) return;
    const onInit = () => {
      setSlideCount(emblaApi.scrollSnapList().length);
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    emblaApi.on('init', onInit);
    emblaApi.on('reInit', onInit);
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('init', onInit);
      emblaApi.off('reInit', onInit);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);
  const MAX_DOTS = 5;
  const getVisibleDots = () => {
    if (slideCount <= MAX_DOTS) {
      return Array.from({
        length: slideCount
      }, (_, i) => i);
    }
    const half = Math.floor(MAX_DOTS / 2);
    if (selectedIndex <= half) {
      return [0, 1, 2, 3, 4];
    }
    if (selectedIndex >= slideCount - half - 1) {
      return Array.from({
        length: MAX_DOTS
      }, (__0, i_0) => slideCount - MAX_DOTS + i_0);
    }
    return Array.from({
      length: MAX_DOTS
    }, (__1, i_1) => selectedIndex - half + i_1);
  };

  /* ---------------- Filter Logic ---------------- */

  useEffect(() => {
    if (user?.id && pageType === 'heart') {
      setFilter('unseen');
    }
  }, [user]);
  const loadHearts = async () => {
    if (!communityId) return;
    try {
      const res = await fetch(`/api/hearts/community/${communityId}`, {
        method: 'GET',
        cache: 'no-store'
      });
      if (!res.ok) throw new Error('Failed to load hearts');
      const json = await res.json();
      const data = (json.data || []).filter(h => h.id !== entity?.id).map(h_0 => {
        const dist = haversineMiles(entity.locationCoordinates, h_0.locationCoordinates || [0, 0]);
        const distanceMiles = Number.isFinite(dist) ? Math.round(dist * 10) / 10 : null;
        const distanceMinutes = minutesFromMiles(distanceMiles);
        return {
          ...h_0,
          distanceMiles,
          distanceMinutes
        };
      }).sort((a, b) => {
        if (pageType === 'heart') {
          return (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity);
        }
        return (a.distanceMinutes ?? Infinity) - (b.distanceMinutes ?? Infinity);
      });
      setAllHearts(data);
    } catch (err) {
      console.error(err);
      setAllHearts([]);
    }
  };
  useEffect(() => {
    loadHearts();
  }, [communityId, entity]);
  useEffect(() => {
    let filtered = allHearts;
    if (filter === 'unseen' && user?.id) {
      filtered = allHearts.filter(h_1 => h_1.isVisited === false);
    }
    if (filtered.length === 0) {
      if (filter === 'unseen') {
        setEmptyMessage("You've seen all the Hearts here!");
      }
    } else {
      setEmptyMessage('');
    }
    setDisplayHearts(filtered);
  }, [filter, allHearts, user?.id]);
  if (allHearts.length < 2) {
    return null;
  }
  return <>
      <Image src={Abstractcontourpattern} alt="Abstract organic wave pattern with red and teal layered contours" width="1137" height="1264" aria-hidden="true" className="sm:block z-[-2] hidden h-auto absolute pointer-events-none left-0 right-0 lg:top-12 top-10 bottom-auto xl:w-[60%] lg:w-[75%] w-[85%]" />
      <Image src={MoAbstractcontourpattern} alt="In mobile abstract organic wave pattern with red and teal layered contours" width="452" height="315" className="sm:hidden block h-auto absolute left-auto right-0 -top-8 bottom-auto" />
      <section className="2xl:py-10 xl:py-7 lg:py-4 py-2 overflow-hidden">
        {/* Header */}
        <div className="container-wrap xl:mb-2 lg:mb-1">
          <div className="flex items-center justify-between gap-6">
            <div className="flex md:flex-row flex-col md:items-center xl:gap-8 md:gap-5 gap-3">
              <h2 className="text-[#323232] font-semibold xl:text-[34px] text-[26px] leading-[1em]">
                Hearts Nearby
              </h2>
              {user && <Toggle options={[{
              label: "Haven't Seen",
              value: 'unseen'
            }, {
              label: 'All Hearts',
              value: 'all'
            }]} defaultValue={filter} onChange={setFilter} />}
            </div>
          </div>
        </div>

        {emptyMessage && <div className="container-wrap z-1 relative">{emptyMessage}</div>}

        {/* Carousel */}
        {!emptyMessage && <div className="relative overflow-hidden !print:max-w-full !print:w-full" style={{
        maxWidth
      }}>
            <div ref={containerRef} className="w-full text-[20px] max-w-[87.4em] mx-auto 2xl:px-[3.2em] xl:px-[2.5em] lg:px-[1.2em] px-0">
              <div ref={emblaRef} className="overflow-hidden lg:py-5 md:py-4 py-3  !print:max-w-full !print:w-full" style={{
            width: carouselWidth
          }}>
                <div className="flex -ml-2 pr-2">
                  {displayHearts?.map(item => <div key={item.id} className="flex-[0_0_auto] md:p-2 p-1.5">
                      <HeartCard data={item} communityName={communityName} communityState={communityState} pageType={pageType} />
                    </div>)}
                </div>
              </div>

              {/* DOTS */}
              <div className="flex lg:hidden justify-center gap-2 mt-1 pb-2">
                {getVisibleDots().map(index => <button key={index} onClick={() => emblaApi?.scrollTo(index)} aria-label={`Go to slide ${index + 1}`} className={`h-1.5 rounded-full transition-all cursor-pointer duration-300
                  ${index === selectedIndex ? 'w-4.25 bg-[#323232]' : 'w-1.5 bg-black/20'}`} />)}
              </div>
            </div>

            {/* Arrows */}
            <div className="hidden lg:flex">
              {hasScrolled && canPrev && <button onClick={scrollPrev} className="2xl:w-12.5 2xl:h-25 xl:w-11 xl:h-22 w-10 h-20 cursor-pointer rounded-[0_10em_10em_0] bg-[#00EAFD] flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2" aria-label="Previous">
                  <span className="sr-only">Previous</span>
                  <IconArrow className="text-white w-6 rotate-180" />
                </button>}

              {canNext && <button onClick={scrollNext} className="2xl:w-12.5 2xl:h-25 xl:w-11 xl:h-22 w-10 h-20 cursor-pointer rounded-[10em_0_0_10em] bg-[#00EAFD] flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2" aria-label="Next">
                  <span className="sr-only">next</span>
                  <IconArrow className="text-white w-6" />
                </button>}
            </div>
          </div>}
      </section>
    </>;
}